import { Router } from '@angular/router';
import { NavigationResult, ResourceResult } from './command-palette.service';

export interface QuickAction {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly shortcut?: string;
  readonly dangerous?: boolean;
}

export interface QuickActionContext {
  readonly router: Router;
  readonly orgId: string;
  readonly clusterId?: string;
  readonly workspaceName?: string;
}

export interface ExecutableQuickAction extends QuickAction {
  /**
   * Offered only for results found inside a workspace. The detail pages (logs and
   * friends) exist only below a workspace route; at cluster level there is no page
   * to send the user to.
   */
  readonly requiresWorkspaceScope?: boolean;
  execute: (item: ResourceResult | NavigationResult, ctx: QuickActionContext) => void;
}

/**
 * The detail page of a workload inside the workspace it was found in. Same route the
 * workspace resource table links to: kind as written by Kubernetes, apiVersion as the
 * group segment.
 */
function navigateToWorkloadPage(resource: ResourceResult, ctx: QuickActionContext, page: string): void {
  ctx.router.navigate([
    '/',
    'studio',
    'organization',
    ctx.orgId,
    'cluster',
    resource.scope.clusterId,
    'workspaces',
    resource.scope.name,
    'resources',
    'namespace',
    resource.namespace,
    'workload',
    resource.kind,
    resource.name,
    resource.apiVersion,
    page
  ]);
}

const VIEW_ACTION: ExecutableQuickAction = {
  id: 'view',
  label: 'View',
  icon: 'pi pi-eye',
  execute: (item) => {
    (item as ResourceResult).execute();
  }
};

const LOGS_ACTION: ExecutableQuickAction = {
  id: 'logs',
  label: 'Logs',
  icon: 'pi pi-desktop',
  shortcut: 'l',
  requiresWorkspaceScope: true,
  execute: (item, ctx) => navigateToWorkloadPage(item as ResourceResult, ctx, 'logs')
};

// Shell has no route (it is a modal on the resource table) and Restart only ever
// opened the resource view, so neither is offered until they can do what they say.
const WORKLOAD_ACTIONS: ExecutableQuickAction[] = [VIEW_ACTION, LOGS_ACTION];

const DEFAULT_RESOURCE_ACTIONS: ExecutableQuickAction[] = [VIEW_ACTION];

const CLUSTER_ACTIONS: ExecutableQuickAction[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'pi pi-objects-column',
    execute: (item) => (item as NavigationResult).openPage('overview')
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'pi pi-cog',
    shortcut: 's',
    execute: (item) => (item as NavigationResult).openPage('settings')
  }
];

const WORKSPACE_ACTIONS: ExecutableQuickAction[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'pi pi-objects-column',
    execute: (item) => (item as NavigationResult).openPage('dashboard')
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: 'pi pi-th-large',
    shortcut: 'r',
    execute: (item) => (item as NavigationResult).openPage('resources')
  }
];

export function getActionsForResource(resource: ResourceResult): ExecutableQuickAction[] {
  let actions: ExecutableQuickAction[];

  switch (resource.kind.toLowerCase()) {
    case 'deployment':
    case 'statefulset':
    case 'daemonset':
    case 'replicaset':
    case 'pod':
      actions = WORKLOAD_ACTIONS;
      break;
    default:
      actions = DEFAULT_RESOURCE_ACTIONS;
  }

  return resource.scope.kind === 'workspace' ? actions : actions.filter((a) => !a.requiresWorkspaceScope);
}

export function getActionsForNavigation(type: 'cluster' | 'workspace'): ExecutableQuickAction[] {
  switch (type) {
    case 'cluster':
      return CLUSTER_ACTIONS;
    case 'workspace':
      return WORKSPACE_ACTIONS;
    default:
      return [];
  }
}

export function findActionByShortcut(actions: ExecutableQuickAction[], key: string): ExecutableQuickAction | undefined {
  return actions.find((a) => a.shortcut === key.toLowerCase());
}
